import { ApiError } from 'api/http/types';
import {
  ApiName,
  getErrorReasonType,
  isBusinessError,
  isPromoCodeError,
} from 'scenes/common/api/quote';
import { unmarshalQuoteErrorResponse } from 'scenes/common/api/quote/marshaller';
import { WireErrorCode } from 'scenes/common/api/quote/types';
import {
  makeApiError,
  makePromoCodeError,
} from 'scenes/common/helpers/apiData';
import {
  ApiData,
  ApiErrorReasonType,
  ApiState,
  BusinessErrorDetail,
} from 'scenes/common/types';
import analytics from 'scenes/motor/analytics';
import { hasPromoCode } from 'scenes/motor/selectors';
import {
  MotorFunnelState,
  MotorQuoteDetails,
  ReferenceData,
} from 'scenes/motor/types';
import { WireMotorQuotePayload, WireMotorQuoteResponse } from '../types';

const InvalidSumInsuredForCoverTypeCode = 'REF103';

const isInvalidPromoCode = (
  funnelState: MotorFunnelState,
  payload: WireMotorQuotePayload
): boolean => hasPromoCode(funnelState) && payload && !payload.campaigns;

export const checkPromoCode = (
  funnelState: MotorFunnelState,
  response: WireMotorQuoteResponse
): WireMotorQuoteResponse | never => {
  if (isInvalidPromoCode(funnelState, response.payload)) {
    throw new ApiError('Invalid promoCode', ApiState.BusinessError, {
      type: ApiErrorReasonType.IneligiblePromoCode,
    });
  }
  return response;
};

const isMotorEmbargo = (details: BusinessErrorDetail): boolean =>
  details.ref === 'REF047' && details.code === WireErrorCode.Declined;

export const isThirdPartySumInsuredError = (
  details: BusinessErrorDetail | undefined
): boolean =>
  details?.ref === InvalidSumInsuredForCoverTypeCode &&
  details?.code === WireErrorCode.Declined &&
  details?.type === 'Referral';

const isMotorErrorType = (details: BusinessErrorDetail) =>
  isMotorEmbargo(details) || isThirdPartySumInsuredError(details);

const getMotorErrorReasonType = (
  details: BusinessErrorDetail
): ApiErrorReasonType => {
  if (isMotorEmbargo(details)) {
    return ApiErrorReasonType.Embargo;
  }
  if (isThirdPartySumInsuredError(details)) {
    return ApiErrorReasonType.InvalidCoverForSumInsured;
  }
  return ApiErrorReasonType.Unknown;
};

const getQuoteError = (
  error: ApiError<BusinessErrorDetail>
): [string, ApiErrorReasonType] => {
  if (!error.data) {
    return ['', ApiErrorReasonType.Unknown];
  }

  const reasonType = isMotorErrorType(error.data)
    ? getMotorErrorReasonType(error.data)
    : getErrorReasonType(error.data);
  return [error.data.id, reasonType];
};

export const handleError = (
  err: ApiError<BusinessErrorDetail>,
  apiName: ApiName,
  funnelState: MotorFunnelState,
  referenceData: ReferenceData
): ApiData<MotorQuoteDetails> => {
  if (isBusinessError(err)) {
    const [quoteNumber, reason] = getQuoteError(err);

    analytics().error(
      funnelState,
      `${apiName}: ${reason}`,
      ApiState.BusinessError,
      quoteNumber
    );

    analytics().referral(funnelState, quoteNumber, reason, referenceData);

    return isPromoCodeError(reason)
      ? makePromoCodeError(
          reason,
          funnelState.apiResponses.quoteDetails.data?.payload.policy.number ??
            quoteNumber,
          funnelState.apiResponses.quoteDetails
        )
      : unmarshalQuoteErrorResponse(
          reason,
          quoteNumber,
          // Non-blocking error should be limited to Third Party Sum Insured error on create quote.
          // TODO: implement for update quote.
          !(
            isThirdPartySumInsuredError(err.data) &&
            apiName === ApiName.CreateQuote
          ),
          apiName
        );
  }

  analytics().error(funnelState, `${apiName}: ${err.message}`);
  return makeApiError(err.message, funnelState.apiResponses.quoteDetails);
};
